import "./NewCircumferenceRecord.css";
import "bootstrap/dist/css/bootstrap.min.css";

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import ChevronRightIcon from "../../assets/imgs/chevron-right-icon.svg";
import DefaultButton from "../../components/DefaultButton/DefaultButton";
import DefaultDropdown from "../../components/DefaultDropdown/DefaultDropdown";
import DefaultModal from "../../components/DefaultModal/DefaultModal";
import TextInput from "../../components/TextInput/TextInput";
import loginService from "../../services/login-service";
import measurementService from "../../services/measurement-service";
import userService from "../../services/user-service";
import { GlobalContext } from "../GlobalContext";
import { buildAnthropometryHubPath } from "./draft-session-utils";
import {
  clearPendingSection,
  readPendingEdit,
  writePendingSection,
} from "./edit-pending-utils";
import { useAutoSave } from "./useAutoSave";
import {
  REGION_GROUPS,
  buildSavePayload,
  createCardFromCatalog,
  formatVisitDateLabel,
  getAvailableRegions,
  getStarterCards,
  resolveCardsForForm,
  toApiDate,
  todayDisplayDate,
} from "./circumference-utils";
const CircumferenceRegionCard = ({
  card,
  onChangeValue,
  onRemove,
  removable = true,
  inputType = "decimal",
}) => {
  const unit = card.unit || "cm";

  return (
    <div className={`circumference-region-card circumference-region-card--${card.group}`}>
      <div className="circumference-region-card__top">
        <div className="circumference-region-card__titles">
          <h3 className="circumference-region-card__title">
            {card.label}
          </h3>
        </div>
        {removable && onRemove && (
          <button
            type="button"
            className="circumference-region-card__remove"
            onClick={() => onRemove(card.key)}
            aria-label={`Remover ${card.label}`}
          >
            Remover
          </button>
        )}
      </div>
      <div className="circumference-region-card__input">
        <label className="circumference-region-card__input-label">
          Medida ({unit})
        </label>
        <TextInput
          placeholder="0"
          value={card.value}
          onChangeText={(value) => onChangeValue(card.key, value)}
          type={inputType}
          size="small"
        />
      </div>
    </div>
  );
};

const NewCircumferenceRecord = () => {
  const { id: patientId } = useParams();
  const [searchParams] = useSearchParams();
  const { setSelectedScreen, setIsLoading } = useContext(GlobalContext);

  const isEditMode = searchParams.get("mode") === "edit";
  const isDraftSession = searchParams.get("mode") === "new";
  const measurementIdParam = searchParams.get("measurementId");
  const isUpdatingExisting = Boolean(measurementIdParam);

  const [patient, setPatient] = useState();
  const [currentUser, setCurrentUser] = useState();
  const [visitDate, setVisitDate] = useState(todayDisplayDate());
  const [weightKg, setWeightKg] = useState("");
  const [heightCm, setHeightCm] = useState("");
  const [selectedCards, setSelectedCards] = useState(() => getStarterCards());
  const [initialSnapshot, setInitialSnapshot] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [draftMeasureLabel, setDraftMeasureLabel] = useState("");
  const [draftMeasureValue, setDraftMeasureValue] = useState("");
  const [formError, setFormError] = useState("");
  const [addModalError, setAddModalError] = useState("");
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const skipAutoSaveRef = useRef(true);

  const currentSnapshot = useMemo(
    () =>
      JSON.stringify({
        visitDate,
        weightKg,
        heightCm,
        selectedCards,
      }),
    [visitDate, weightKg, heightCm, selectedCards]
  );

  const isDirty = currentSnapshot !== initialSnapshot;

  const hasAtLeastOneValue = useMemo(
    () =>
      selectedCards.some(
        (card) => card.value !== "" && card.value !== null && card.value !== undefined
      ),
    [selectedCards]
  );

  const hasPersistableData = useMemo(
    () =>
      hasAtLeastOneValue ||
      (weightKg !== "" && weightKg != null) ||
      (heightCm !== "" && heightCm != null),
    [hasAtLeastOneValue, weightKg, heightCm]
  );

  const availableRegions = useMemo(
    () => getAvailableRegions(selectedCards, REGION_GROUPS.CIRCUMFERENCE),
    [selectedCards]
  );

  const availableOptions = useMemo(
    () => availableRegions.map((item) => item.label),
    [availableRegions]
  );

  const resolveRegionFromOption = (optionLabel) => {
    const baseLabel = (optionLabel || "").trim();
    return availableRegions.find((item) => item.label === baseLabel)
      || getAvailableRegions([], REGION_GROUPS.CIRCUMFERENCE).find((item) => item.label === baseLabel);
  };

  const canSave = isDirty && hasAtLeastOneValue && visitDate?.length === 10;
  const canAddMore = availableRegions.length > 0;

  const hubPath = useMemo(() => {
    if (measurementIdParam) {
      return `/patients/${patientId}/anthropometry?measurementId=${measurementIdParam}`;
    }
    if (isDraftSession) {
      return buildAnthropometryHubPath(patientId, { mode: "new" });
    }
    return `/patients/${patientId}/anthropometry`;
  }, [isDraftSession, measurementIdParam, patientId]);

  const buildCurrentPayload = useCallback(
    () =>
      buildSavePayload({
        nutritionistId: currentUser?.id,
        patientId,
        visitDate: toApiDate(visitDate),
        selectedCards,
        patient,
        weightKg,
        heightCm,
        measurementId: isUpdatingExisting ? measurementIdParam : undefined,
      }),
    [
      currentUser?.id,
      patientId,
      visitDate,
      selectedCards,
      patient,
      weightKg,
      heightCm,
      isUpdatingExisting,
      measurementIdParam,
    ]
  );

  const buildPendingSectionData = useCallback(
    () => ({
      payload: buildCurrentPayload(),
      formState: {
        selectedCards,
        weightKg,
        heightCm,
        visitDate,
      },
    }),
    [buildCurrentPayload, selectedCards, weightKg, heightCm, visitDate]
  );

  const persistPending = useCallback(
    async (data) => {
      writePendingSection(patientId, measurementIdParam, "circumference", data);
    },
    [patientId, measurementIdParam]
  );

  const { saveStateLabel, scheduleSave, cancelPending, flush } = useAutoSave({
    persist: persistPending,
  });

  useEffect(() => {
    skipAutoSaveRef.current = true;
  }, [measurementIdParam, patientId]);

  useEffect(() => {
    if (isDraftSession && !measurementIdParam) {
      window.location.href = `/patients/${patientId}/anthropometry`;
    }
  }, [isDraftSession, measurementIdParam, patientId]);

  const fetchData = useCallback(async () => {
    skipAutoSaveRef.current = true;
    setIsLoading(true);
    try {
      const [userFound, patientFound, histories] = await Promise.all([
        loginService.getCurrentUser(),
        userService.getUserById(patientId),
        measurementService.listByPatient(patientId),
      ]);

      setCurrentUser(userFound);
      setPatient(patientFound);

      const sourceMeasurement = isUpdatingExisting
        ? histories?.find((item) => String(item.id) === String(measurementIdParam))
        : null;

      const pending = isUpdatingExisting
        ? readPendingEdit(patientId, measurementIdParam)
        : null;
      const pendingForm = pending?.circumference?.formState;

      let nextCards = isUpdatingExisting && sourceMeasurement
        ? resolveCardsForForm(sourceMeasurement)
        : getStarterCards();
      let nextVisitDate = isUpdatingExisting && sourceMeasurement?.visit_date
        ? formatVisitDateLabel(sourceMeasurement.visit_date)
        : todayDisplayDate();
      let nextWeight =
        sourceMeasurement?.metabolic_data?.weight_kg ??
        patientFound?.body_measurement?.weight ??
        "";
      let nextHeight =
        sourceMeasurement?.metabolic_data?.height_cm ??
        patientFound?.body_measurement?.height ??
        "";

      if (pendingForm) {
        nextCards = pendingForm.selectedCards ?? nextCards;
        nextVisitDate = pendingForm.visitDate ?? nextVisitDate;
        nextWeight = pendingForm.weightKg ?? nextWeight;
        nextHeight = pendingForm.heightCm ?? nextHeight;
      }

      setSelectedCards(nextCards);
      setVisitDate(nextVisitDate);
      setWeightKg(nextWeight === "" || nextWeight == null ? "" : String(nextWeight));
      setHeightCm(nextHeight === "" || nextHeight == null ? "" : String(nextHeight));
      setInitialSnapshot(
        JSON.stringify({
          visitDate: nextVisitDate,
          weightKg: nextWeight === "" || nextWeight == null ? "" : String(nextWeight),
          heightCm: nextHeight === "" || nextHeight == null ? "" : String(nextHeight),
          selectedCards: nextCards,
        })
      );
    } catch (error) {
      console.error(error);
      const fallbackCards = getStarterCards();
      const nextVisitDate = todayDisplayDate();
      setSelectedCards(fallbackCards);
      setVisitDate(nextVisitDate);
      setWeightKg("");
      setHeightCm("");
      setInitialSnapshot(
        JSON.stringify({
          visitDate: nextVisitDate,
          weightKg: "",
          heightCm: "",
          selectedCards: fallbackCards,
        })
      );
      setFormError("Não foi possível carregar o histórico. Você pode registrar as medidas base.");
    } finally {
      setIsLoading(false);
      skipAutoSaveRef.current = false;
    }
  }, [patientId, setIsLoading, isUpdatingExisting, measurementIdParam]);

  useEffect(() => {
    document.title = isEditMode
      ? "Editar registro de circunferências"
      : "Novo registro de circunferências";
    setSelectedScreen("patients");
    fetchData();
  }, [fetchData, setSelectedScreen, isEditMode]);

  useEffect(() => {
    const canAutoSave =
      (isDraftSession || isEditMode) && measurementIdParam && currentUser && isDirty;
    if (!canAutoSave) {
      return;
    }
    if (skipAutoSaveRef.current) {
      return;
    }
    if (!hasPersistableData) {
      clearPendingSection(patientId, measurementIdParam, "circumference");
      return;
    }
    scheduleSave(buildPendingSectionData());
  }, [
    buildPendingSectionData,
    currentUser,
    hasPersistableData,
    isDirty,
    isDraftSession,
    isEditMode,
    measurementIdParam,
    patientId,
    scheduleSave,
    selectedCards,
    visitDate,
    weightKg,
    heightCm,
  ]);

  const handleReturnToDraftHub = () => {
    window.location.href = buildAnthropometryHubPath(patientId, {
      mode: "new",
      measurementId: measurementIdParam,
    });
  };

  const handleDiscardChanges = async () => {
    if (!isDirty) {
      handleReturnToDraftHub();
      return;
    }

    if (!initialSnapshot) {
      return;
    }

    cancelPending();

    const parsed = JSON.parse(initialSnapshot);
    const baselinePayload = buildSavePayload({
      nutritionistId: currentUser?.id,
      patientId,
      visitDate: toApiDate(parsed.visitDate),
      selectedCards: parsed.selectedCards,
      patient,
      weightKg: parsed.weightKg,
      heightCm: parsed.heightCm,
      measurementId: measurementIdParam,
    });
    const hasBaselineData =
      parsed.selectedCards.some(
        (card) => card.value !== "" && card.value != null && card.value !== undefined
      ) ||
      parsed.weightKg !== "" ||
      parsed.heightCm !== "";

    if (hasBaselineData) {
      writePendingSection(patientId, measurementIdParam, "circumference", {
        payload: baselinePayload,
        formState: {
          selectedCards: parsed.selectedCards,
          weightKg: parsed.weightKg,
          heightCm: parsed.heightCm,
          visitDate: parsed.visitDate,
        },
      });
    } else {
      clearPendingSection(patientId, measurementIdParam, "circumference");
    }

    handleReturnToDraftHub();
  };

  const handleReturnToAnthropometry = () => {
    window.location.href = hubPath;
  };

  const handleSaveAndReturn = async () => {
    if (!isDirty) {
      if (isDraftSession) {
        handleReturnToDraftHub();
      } else {
        handleReturnToAnthropometry();
      }
      return;
    }

    if (!canSave) {
      setFormError("Preencha ao menos uma medida válida para continuar.");
      return;
    }

    if (hasPersistableData) {
      await persistPending(buildPendingSectionData());
    }
    await flush();

    if (isDraftSession) {
      handleReturnToDraftHub();
    } else {
      handleReturnToAnthropometry();
    }
  };

  const handleCardValueChange = (key, value) => {
    setSelectedCards((current) =>
      current.map((card) =>
        card.key === key
          ? {
              ...card,
              value,
            }
          : card
      )
    );
    setFormError("");
  };

  const handleRemoveCard = (key) => {
    setSelectedCards((current) => current.filter((card) => card.key !== key));
    setFormError("");
  };

  const resetAddModal = () => {
    setDraftMeasureLabel("");
    setDraftMeasureValue("");
    setAddModalError("");
  };

  const handleOpenAddModal = () => {
    if (!canAddMore) {
      return;
    }
    resetAddModal();
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    resetAddModal();
  };

  const draftRegion = resolveRegionFromOption(draftMeasureLabel);

  const handleConfirmAddMeasure = () => {
    const region = resolveRegionFromOption(draftMeasureLabel);
    if (!region) {
      setAddModalError("Selecione uma medida válida do catálogo.");
      return;
    }

    const alreadySelected = selectedCards.some((card) => card.key === region.key);
    if (alreadySelected) {
      setAddModalError("Essa medida já foi adicionada.");
      return;
    }

    setSelectedCards((current) => [
      ...current,
      createCardFromCatalog(region, draftMeasureValue),
    ]);
    setFormError("");
    handleCloseAddModal();
  };

  const cardsByGroup = useMemo(() => {
    const groups = [{ id: REGION_GROUPS.CIRCUMFERENCE }];

    return groups
      .map((group) => ({
        ...group,
        cards: selectedCards.filter((card) => card.group === group.id),
      }))
      .filter((group) => group.cards.length > 0);
  }, [selectedCards]);

  return (
    <div className="new-circumference-record">
      <div className="new-circumference-record__top">
        <div className="new-circumference-record__breadcrumb">
        <a href="/patients" className="new-circumference-record__breadcrumb__link">
          Pacientes
        </a>
        <div className="new-circumference-record__breadcrumb__divisor">
          <img src={ChevronRightIcon} alt="" />
        </div>
        <a
          href={`/patients/${patientId}`}
          className="new-circumference-record__breadcrumb__link"
        >
          {patient?.name || "Paciente"}
        </a>
        <div className="new-circumference-record__breadcrumb__divisor">
          <img src={ChevronRightIcon} alt="" />
        </div>
        <a
          href={hubPath}
          className="new-circumference-record__breadcrumb__link"
        >
          Antropometria
        </a>
        <div className="new-circumference-record__breadcrumb__divisor">
          <img src={ChevronRightIcon} alt="" />
        </div>
        <p
          className={`new-circumference-record__breadcrumb__current ${
            isDraftSession ? "new-circumference-record__breadcrumb__current--draft" : ""
          }`}
        >
          {isEditMode ? "Editar" : isDraftSession ? "Rascunho" : "Novo registro"}
        </p>
        </div>
      </div>

      <div className="new-circumference-record__shell">
        <div className="new-circumference-record__hero">
          <div className="new-circumference-record__hero__copy">
            <p className="new-circumference-record__hero__eyebrow">Antropometria</p>
            <h1>
              {isEditMode
                ? "Editar registro de circunferências"
                : "Novo registro de circunferências"}
            </h1>
            <p>
              {isEditMode
                ? "Atualize as circunferências deste registro. As alterações são salvas automaticamente nesta tela e só entram no histórico ao salvar no hub."
                : "Registre circunferências corporais (cm). As alterações são salvas automaticamente nesta tela e só entram no histórico ao salvar no hub."}
            </p>
          </div>
          <div className="new-circumference-record__date">
            <p className="new-circumference-record__date__label">Data da medição</p>
            <p className="new-circumference-record__date__value">{visitDate}</p>
            {(isDraftSession || isEditMode) && saveStateLabel && (
              <p className="new-circumference-record__autosave">{saveStateLabel}</p>
            )}
          </div>
        </div>

        <div className="new-circumference-record__section new-circumference-record__body-section">
          <h3>Peso e altura</h3>
          <div className="new-circumference-record__cards-grid">
            <CircumferenceRegionCard
              card={{
                key: "weight",
                label: "Peso",
                group: "body",
                unit: "kg",
                value: weightKg,
              }}
              onChangeValue={(_, value) => setWeightKg(value)}
              removable={false}
            />
            <CircumferenceRegionCard
              card={{
                key: "height",
                label: "Altura",
                group: "body",
                unit: "cm",
                value: heightCm,
              }}
              onChangeValue={(_, value) => setHeightCm(value)}
              removable={false}
              inputType="number"
            />
          </div>
        </div>

        <div className="new-circumference-record__toolbar">
          <div>
            <div className="new-circumference-record__toolbar__heading">
              <h2>Medidas selecionadas</h2>
              <button
                type="button"
                className="new-circumference-record__add-button"
                onClick={handleOpenAddModal}
                disabled={!canAddMore}
                aria-label="Adicionar medida"
                title="Adicionar medida"
              >
                <span className="new-circumference-record__add-button__icon" aria-hidden="true" />
              </button>
            </div>
            <p>{selectedCards.length} medida(s) no registro</p>
          </div>
        </div>

        <div className="new-circumference-record__tip">
          <p>
            <strong>Dica:</strong> o registro inicia com CC, CQ, CB e CPant. Use o botão +
            para incluir outras circunferências (CA, CP, CMB, coxa, punho, tórax). Cada
            medida só pode aparecer uma vez.
          </p>
        </div>

        {cardsByGroup.map((group) => (
          <div key={group.id} className="new-circumference-record__section">
            <div className="new-circumference-record__cards-grid">
              {group.cards.map((card) => (
                <CircumferenceRegionCard
                  key={card.key}
                  card={card}
                  onChangeValue={handleCardValueChange}
                  onRemove={handleRemoveCard}
                />
              ))}
            </div>
          </div>
        ))}

        {formError && (
          <p className="new-circumference-record__error">{formError}</p>
        )}

        {(isDraftSession || isEditMode) && (
          <div className="new-circumference-record__warning">
            <p>
              As alterações são salvas automaticamente nesta tela. Use Salvar no hub de
              antropometria para aplicar ao histórico.
            </p>
          </div>
        )}

        {(isDraftSession || isEditMode) && (
          <div className="new-circumference-record__footer">
            {isDraftSession ? (
              <>
                <DefaultButton
                  title="Descartar e voltar"
                  type="secondary"
                  onClick={() => setShowDiscardModal(true)}
                />
                <DefaultButton
                  title="Salvar"
                  type="primary"
                  onClick={handleSaveAndReturn}
                  disabled={!isDirty}
                />
              </>
            ) : (
              <>
                <DefaultButton
                  title="Voltar"
                  type="secondary"
                  onClick={handleReturnToAnthropometry}
                />
                <DefaultButton
                  title="Salvar"
                  type="primary"
                  onClick={handleSaveAndReturn}
                  disabled={!isDirty}
                />
              </>
            )}
          </div>
        )}
      </div>

      {isDraftSession && (
        <DefaultModal
          title={isDirty ? "Descartar alterações" : "Voltar ao hub"}
          showModal={showDiscardModal}
          onClose={() => setShowDiscardModal(false)}
        >
          <div className="new-circumference-record__confirm">
            <p>
              {isDirty
                ? "As alterações desta tela serão revertidas e você voltará ao hub. O registro será mantido."
                : "Nenhuma alteração foi feita nesta tela. Deseja voltar ao hub?"}
            </p>
            <div className="new-circumference-record__confirm__actions">
              <DefaultButton
                title="Cancelar"
                type="secondary"
                onClick={() => setShowDiscardModal(false)}
              />
              <DefaultButton
                title={isDirty ? "Descartar e voltar" : "Voltar ao hub"}
                type="primary"
                onClick={() => {
                  setShowDiscardModal(false);
                  handleDiscardChanges();
                }}
              />
            </div>
          </div>
        </DefaultModal>
      )}

      <DefaultModal
        title="Adicionar medida"
        showModal={showAddModal}
        onClose={handleCloseAddModal}
        className="new-circumference-record__add-modal"
      >
        <div className="new-circumference-record__add">
          <p className="new-circumference-record__add__hint">
            Escolha a medida do catálogo e preencha o valor no card abaixo.
          </p>

          <div className="new-circumference-record__add__field">
            <p className="new-circumference-record__add__label">Medida</p>
            <DefaultDropdown
              placeholder={canAddMore ? "Selecionar medida" : "Catálogo completo"}
              value={draftMeasureLabel}
              options={availableOptions}
              onSelect={(optionLabel) => {
                setDraftMeasureLabel(optionLabel);
                setAddModalError("");
              }}
              disabled={!canAddMore}
              className="new-circumference-record__region-dropdown"
            />
          </div>

          {draftRegion ? (
            <div className="new-circumference-record__add__preview">
              <CircumferenceRegionCard
                card={createCardFromCatalog(draftRegion, draftMeasureValue)}
                onChangeValue={(_, value) => setDraftMeasureValue(value)}
                removable={false}
              />
            </div>
          ) : (
            <p className="new-circumference-record__add__hint">
              Selecione uma medida para editar o card.
            </p>
          )}

          {addModalError && (
            <p className="new-circumference-record__error new-circumference-record__error--modal">
              {addModalError}
            </p>
          )}

          <div className="new-circumference-record__add__actions">
            <DefaultButton
              title="Cancelar"
              type="secondary"
              onClick={handleCloseAddModal}
            />
            <DefaultButton
              title="Adicionar"
              type="primary"
              disabled={!draftRegion}
              onClick={handleConfirmAddMeasure}
            />
          </div>
        </div>
      </DefaultModal>
    </div>
  );
};

export default NewCircumferenceRecord;
